import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Badge, Alert, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faRocket, 
  faCreditCard, 
  faCheckCircle,
  faGlobe,
  faUsers,
  faRobot,
  faDollarSign,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import { loadStripe } from '@stripe/stripe-js';
import './AgentUnderBrokerSubscription.css';

// Type definitions
interface AIUpgradePlan {
  id: string;
  name: string;
  description: string;
  additionalCost: number;
  totalCost: number;
  stripePrice?: string;
  features: string[];
  included?: boolean;
  highlight?: boolean;
  savings?: string;
  individualPrice?: number;
}

interface AIUpgrades {
  [key: string]: AIUpgradePlan;
}

// Agent Under Broker AI Upgrade Tiers - Matching actual pricing
const AGENT_AI_UPGRADES: AIUpgrades = {
  base: {
    id: 'agent-under-broker-base',
    name: 'Base CRM',
    description: 'Essential tools provided by your brokerage',
    additionalCost: 0,
    totalCost: 0,
    features: [
      'Lead Management System',
      'Deal Pipeline Tracking',
      'Contact Database',
      'Team Communication',
      'Mobile App Access',
      'Basic Reporting'
    ],
    included: true
  },
  professional: {
    id: 'professional-ai',
    name: 'Professional AI',
    description: 'AI Chat + Professional Website',
    additionalCost: 180,
    totalCost: 180,
    stripePrice: 'price_agent_professional_180', // TODO: Replace with actual Stripe price ID
    features: [
      'Everything in Base CRM',
      '🤖 AI Chat Assistant',
      '🌐 Professional Agent Website',
      '🤖 AI Lead Insights',
      '🤖 Smart Follow-ups',
      '🏠 Kairos AI Home Search'
    ],
    highlight: true,
    savings: 'Save $70/month vs Individual ($250)',
    individualPrice: 250
  },
  dealAI: {
    id: 'deal-ai',
    name: 'Deal AI',
    description: 'AI-powered transaction management',
    additionalCost: 280,
    totalCost: 280,
    stripePrice: 'price_agent_dealai_280', // TODO: Replace with actual Stripe price ID
    features: [
      'Everything in Professional AI',
      '📄 AI Document Analysis',
      '🤖 Transaction Management AI',
      '📊 Deal Progress Tracking',
      '🤖 Contract Intelligence',
      '✅ Automated Compliance'
    ],
    highlight: false,
    savings: 'Save $70/month vs Individual ($350)',
    individualPrice: 350
  },
  agentK: {
    id: 'agent-k',
    name: 'Agent K',
    description: 'Fully autonomous AI agent',
    additionalCost: 430,
    totalCost: 430,
    stripePrice: 'price_agent_agentk_430', // TODO: Replace with actual Stripe price ID
    features: [
      'Everything in Deal AI',
      '🚀 Fully Autonomous AI Agent',
      '🧠 Advanced Market Intelligence',
      '📈 Predictive Analytics',
      '🤖 24/7 AI Operations',
      '⭐ White-glove Support'
    ],
    highlight: false,
    savings: 'Save $70/month vs Individual ($500)',
    individualPrice: 500
  }
};

// Website Add-on (optional)
const WEBSITE_ADDON = {
  id: 'website-setup',
  name: 'Professional Agent Website',
  description: 'Your own branded website with lead capture',
  price: 500,
  type: 'one-time',
  stripePrice: 'price_website_setup_500', // TODO: Replace with actual Stripe price ID
  features: [
    'Custom domain (yourname.com)',
    'Professional design templates',
    'IDX property search integration',
    'Lead capture forms',
    'Mobile responsive design',
    '30-45 day implementation'
  ],
  note: 'Website included free with Professional AI upgrade'
};

interface AgentUnderBrokerSubscriptionProps {
  userSubscription: any;
  brokerageInfo: {
    id: string;
    name: string;
    maxAgents: number;
    memberRole?: string;
    isOwner?: boolean;
  };
  onSubscriptionUpdate: () => void;
}

const AgentUnderBrokerSubscription: React.FC<AgentUnderBrokerSubscriptionProps> = ({
  userSubscription,
  brokerageInfo,
  onSubscriptionUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [hasWebsite, setHasWebsite] = useState(false);

  // Check if agent already has website add-on
  useEffect(() => {
    checkWebsiteStatus();
  }, []);

  const checkWebsiteStatus = async () => {
    try {
      const response = await axiosInstance.get('/users/website-addon-status');
      setHasWebsite(response.data.hasWebsite);
    } catch (error) {
      console.error('Error checking website status:', error);
    }
  };

  // Determine current AI level
  const getCurrentAILevel = () => {
    const packageName = userSubscription?.subscription_package_name?.toLowerCase() || '';
    if (packageName.includes('agent k') || packageName.includes('agentk')) return 'agentK';
    if (packageName.includes('deal ai') || packageName.includes('dealai')) return 'dealAI';
    if (packageName.includes('professional')) return 'professional';
    return 'base';
  };
  
  const currentAILevel = getCurrentAILevel();

  const handleUpgradeClick = (plan: any) => {
    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const handleUpgradeConfirm = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      // Create Stripe checkout session for agent upgrade
      const response = await axiosInstance.post('/broker/agent-direct-billing', {
        planType: selectedPlan.id,
        priceId: selectedPlan.stripePrice,
        brokerageId: brokerageInfo.id
      });

      if (response.data.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error: any) {
      console.error('Upgrade error:', error);
      alert('Failed to process upgrade. Please try again.');
    } finally {
      setLoading(false);
      setShowUpgradeModal(false);
    }
  };

  const handleWebsiteAddon = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/broker/website-addon-checkout', {
        priceId: WEBSITE_ADDON.stripePrice,
        brokerageId: brokerageInfo.id
      });

      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      console.error('Website addon error:', error);
      alert('Failed to process website addon. Please try again.');
    } finally {
      setLoading(false);
      setShowWebsiteModal(false);
    }
  };

  return (
    <div className="agent-subscription-container">
      {/* Brokerage Info Banner */}
      <Alert variant="info" className="mb-4">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faBuilding} size="2x" className="me-3" />
          <div>
            {brokerageInfo.isOwner ? (
              <>
                <h5 className="mb-1">You own {brokerageInfo.name}</h5>
                <p className="mb-0">
                  As a broker owner, you can upgrade your personal AI capabilities with direct billing starting at +$180/month.
                </p>
              </>
            ) : (
              <>
                <h5 className="mb-1">You're part of {brokerageInfo.name}</h5>
                <p className="mb-0">
                  Your brokerage provides base CRM features. Add AI capabilities with direct billing starting at +$180/month.
                </p>
              </>
            )}
          </div>
        </div>
      </Alert>

      {/* Current Plan Display */}
      <Card className="current-plan-card mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4>Current Plan: {AGENT_AI_UPGRADES[currentAILevel].name}</h4>
              <p className="text-muted mb-0">
                {currentAILevel === 'base' 
                  ? 'Base features provided by your brokerage' 
                  : `AI upgrade: +$${AGENT_AI_UPGRADES[currentAILevel].additionalCost}/month direct billing`
                }
              </p>
            </div>
            <h3 className="mb-0">
              {currentAILevel === 'base' 
                ? 'Included' 
                : `$${AGENT_AI_UPGRADES[currentAILevel].totalCost}/mo`
              }
            </h3>
          </div>
        </Card.Body>
      </Card>

      {/* AI Upgrade Options */}
      <div className="mb-4">
        <h3 className="mb-2">Enable AI for Your Business</h3>
        <p className="text-muted">
          Choose your AI level with direct billing. <strong>Save $70/month</strong> compared to individual plans!
        </p>
      </div>
      
      <Row className="mb-5">
        {Object.entries(AGENT_AI_UPGRADES).map(([key, plan]) => {
          if (key === 'base') return null; // Skip base plan in upgrade options
          
          return (
            <Col lg={4} key={plan.id} className="mb-4">
              <Card className={`h-100 ${plan.highlight ? 'border-primary shadow' : ''}`}>
                {plan.highlight && (
                  <div className="ribbon">
                    <span>MOST POPULAR</span>
                  </div>
                )}
                <Card.Header className="text-center py-4">
                  <h3>{plan.name}</h3>
                  <p className="text-muted mb-2">{plan.description}</p>
                  <div className="price-display">
                    <h2 className="mb-0">+${plan.additionalCost}</h2>
                    <span className="text-muted">/agent/month</span>
                  </div>
                  {plan.savings && (
                    <div className="mt-2">
                      <Badge bg="success">{plan.savings}</Badge>
                      <small className="d-block text-muted mt-1">
                        Individual price: ${plan.individualPrice}/mo
                      </small>
                    </div>
                  )}
                </Card.Header>
                <Card.Body>
                  <ul className="feature-list">
                    {plan.features.slice(1).map((feature, idx) => (
                      <li key={idx} className="mb-2">
                        <FontAwesomeIcon 
                          icon={faCheckCircle} 
                          className="text-success me-2" 
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
                <Card.Footer className="text-center py-3">
                  {currentAILevel === key ? (
                    <Button variant="secondary" disabled>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Current AI Level
                    </Button>
                  ) : currentAILevel === 'base' || 
                     (currentAILevel === 'professional' && (key === 'dealAI' || key === 'agentK')) ||
                     (currentAILevel === 'dealAI' && key === 'agentK') ? (
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={() => handleUpgradeClick(plan)}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faRocket} className="me-2" />
                      {currentAILevel === 'base' ? 'Enable AI' : 'Upgrade'}
                    </Button>
                  ) : (
                    <Button variant="outline-secondary" disabled>
                      Lower tier than current
                    </Button>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Website Add-on Section - Only show if not on Professional AI or higher */}
      {currentAILevel === 'base' && !hasWebsite && (
        <Card className="website-addon-section">
          <Card.Header className="bg-light">
            <h4 className="mb-0">
              <FontAwesomeIcon icon={faGlobe} className="me-2" />
              Standalone Website Option
            </h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="info">
              <FontAwesomeIcon icon={faStar} className="me-2" />
              <strong>Pro tip:</strong> The website is included FREE with Professional AI upgrade!
            </Alert>
            <Row>
              <Col md={8}>
                <h5>{WEBSITE_ADDON.name}</h5>
                <p className="text-muted">{WEBSITE_ADDON.description}</p>
                <Row>
                  <Col md={6}>
                    <ul className="feature-list small">
                      {WEBSITE_ADDON.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="mb-1">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" size="sm" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="feature-list small">
                      {WEBSITE_ADDON.features.slice(3).map((feature, idx) => (
                        <li key={idx} className="mb-1">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" size="sm" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="text-center border-start">
                <h3 className="mb-3">
                  ${WEBSITE_ADDON.price}
                  <small className="text-muted d-block">One-time setup fee</small>
                </h3>
                <Button 
                  variant="outline-primary" 
                  size="lg"
                  onClick={() => setShowWebsiteModal(true)}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faGlobe} className="me-2" />
                  Get Website Only
                </Button>
                <p className="mt-2 text-muted small">
                  Or get it FREE with Professional AI
                </p>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Upgrade Confirmation Modal */}
      <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upgrade to {selectedPlan?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <FontAwesomeIcon icon={faCreditCard} className="me-2" />
            This AI upgrade will be billed directly to you, separate from your brokerage.
          </Alert>
          
          <div className="mb-3">
            <h5>You're upgrading to:</h5>
            <h4>{selectedPlan?.name}</h4>
            <p className="text-muted">{selectedPlan?.description}</p>
          </div>
          
          <div className="p-3 bg-light rounded mb-3">
            <Row>
              <Col xs={6}>
                <strong>Your cost:</strong><br />
                <h4 className="mb-0 text-primary">+${selectedPlan?.additionalCost}/mo</h4>
              </Col>
              <Col xs={6} className="text-end">
                <strong>You save:</strong><br />
                <h4 className="mb-0 text-success">$70/mo</h4>
              </Col>
            </Row>
          </div>
          
          <h6>Key features you'll unlock:</h6>
          <ul className="mb-0">
            {selectedPlan?.features.slice(1, 4).map((feature: string, idx: number) => (
              <li key={idx}>{feature}</li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpgradeModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpgradeConfirm}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm & Pay'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Website Purchase Modal */}
      <Modal show={showWebsiteModal} onHide={() => setShowWebsiteModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Get Your Professional Website</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faStar} className="me-2" />
            <strong>Did you know?</strong> The website is included FREE when you upgrade to Professional AI!
          </Alert>
          
          <Row>
            <Col md={6}>
              <h5>What's included:</h5>
              <ul>
                {WEBSITE_ADDON.features.map((feature, idx) => (
                  <li key={idx} className="mb-2">{feature}</li>
                ))}
              </ul>
            </Col>
            <Col md={6}>
              <div className="p-3 bg-light rounded">
                <h5>Investment Details:</h5>
                <p><strong>Setup Fee:</strong> ${WEBSITE_ADDON.price} (one-time)</p>
                <p><strong>Timeline:</strong> 30-45 days</p>
                <p><strong>Hosting:</strong> First year included</p>
                <p className="mb-0 small text-muted">
                  * Annual hosting renewal: $120/year after first year
                </p>
              </div>
              
              <div className="mt-3 p-3 border rounded">
                <h6 className="text-primary">💡 Better Option:</h6>
                <p className="mb-0">
                  Upgrade to <strong>Professional AI</strong> for +$180/month and get the website included FREE!
                </p>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowWebsiteModal(false);
              handleUpgradeClick(AGENT_AI_UPGRADES.professional);
            }}
          >
            Upgrade to Professional AI Instead
          </Button>
          <Button 
            variant="outline-secondary" 
            onClick={handleWebsiteAddon}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Purchase Website Only'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AgentUnderBrokerSubscription;